import { format } from 'date-fns';
import React, { useState } from 'react';
import { Loader } from 'react-feather';

import {
  useCreateOrderMemo,
  useUpdateOrderAmount,
  useUpdateOrderStatus,
} from '@/Order/hooks/useOrderUpdate';
import { OrderSummarySectionProps } from '@/Order/types/orderTypes';
import {
  roundByCurrency,
  sanitizePriceInput,
  subtractPrices,
  sumPrices,
  validateKRW,
} from '@/shared/utils/priceUtils';

const OrderSummarySection: React.FC<OrderSummarySectionProps> = ({
  orderData,
  onRefetch,
}) => {
  const [totalAmount, setTotalAmount] = useState(
    orderData.finalPaymentAmount.toString(),
  );
  const [discountReason, setDiscountReason] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(orderData.status);
  const [isLoadingAmount, setIsLoadingAmount] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  const updateAmountMutation = useUpdateOrderAmount();
  const createMemoMutation = useCreateOrderMemo();
  const updateStatusMutation = useUpdateOrderStatus();

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss');
  };

  const calculateAmounts = (finalAmount: number) => {
    const currencyCode = orderData.currencyCode as 'KRW' | 'USD';

    const amounts = orderData.orderDetails.map((detail) => detail.amount);
    const beforeDiscountAmount = sumPrices(amounts, currencyCode);

    const discountAmount = subtractPrices(
      beforeDiscountAmount,
      finalAmount,
      currencyCode,
    );

    const afterDiscountAmount = roundByCurrency(finalAmount, currencyCode);

    return {
      beforeDiscountProductAmount: beforeDiscountAmount,
      discountAmount: Math.max(0, discountAmount),
      afterDiscountProductAmount: afterDiscountAmount,
      finalPaymentAmount: afterDiscountAmount,
    };
  };

  const handleSaveAmount = async () => {
    const finalAmount = parseFloat(totalAmount);

    if (isNaN(finalAmount) || finalAmount < 0) {
      alert('Enter a valid total amount.');
      return;
    }

    const currencyCode = orderData.currencyCode as 'KRW' | 'USD';

    if (currencyCode === 'KRW' && !validateKRW(finalAmount)) {
      alert('Only integers are allowed for KRW.');
      return;
    }

    setIsLoadingAmount(true);

    try {
      const amountData = calculateAmounts(finalAmount);
      await updateAmountMutation.mutateAsync({
        orderUuid: orderData.uuid,
        data: amountData,
      });

      if (discountReason.trim()) {
        await createMemoMutation.mutateAsync({
          orderUuid: orderData.uuid,
          data: {
            content: discountReason.trim(),
            type: 'PUBLIC',
          },
        });
      }

      onRefetch();
      setDiscountReason('');
      alert('Order information updated successfully.');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || 'An error occurred while updating.';
      alert(errorMessage);
    } finally {
      setIsLoadingAmount(false);
    }
  };

  const handleSaveStatus = async () => {
    if (selectedStatus === orderData.status) {
      alert('No changes to save.');
      return;
    }

    setIsLoadingStatus(true);

    try {
      await updateStatusMutation.mutateAsync({
        orderUuid: orderData.uuid,
        data: {
          status: selectedStatus,
        },
      });

      onRefetch();
      alert('Order status updated successfully.');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || 'An error occurred while updating.';
      alert(errorMessage);
      setSelectedStatus(orderData.status);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const handleTotalAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currencyCode = orderData.currencyCode as 'KRW' | 'USD';
    const sanitized = sanitizePriceInput(e.target.value, currencyCode);
    setTotalAmount(sanitized);
  };

  const getStatusDisplayValue = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Create Order';
      case 'PAID':
        return 'Completed Payment';
      case 'CANCELLED':
        return 'Completed Order';
      default:
        return status;
    }
  };

  const totalQuantity = orderData.orderDetails.reduce(
    (sum, detail) => sum + detail.quantity,
    0,
  );

  return (
    <div className="flex flex-col rounded border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-[#DDDDDD] p-5">
        <div className="flex items-center gap-2">
          <select
            className="h-8 cursor-pointer rounded border border-[#DDDDDD] px-2 text-sm text-[#222222] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as any)}
            disabled={isLoadingStatus}
          >
            <option value="PENDING">Create Order</option>
            <option value="PAID">Completed Payment</option>
            <option value="CANCELLED">Cancel Order</option>
          </select>
          <button
            className="flex items-center gap-1 rounded border border-[#8F689A] px-2 py-1 text-xs text-[#8F689A] hover:bg-[#8F689A] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            onClick={handleSaveStatus}
            disabled={isLoadingStatus || selectedStatus === orderData.status}
          >
            {isLoadingStatus && <Loader size={12} className="animate-spin" />}
            Save
          </button>
        </div>
        <p className="text-sm text-[#646464]">
          {formatDate(orderData.createdAt)}
        </p>
      </div>
      <div className="p-5">
        <div className="flex justify-end">
          <button
            className="flex items-center gap-2 rounded border border-[#8F689A] px-3 py-1 text-sm text-[#8F689A] hover:bg-[#8F689A] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            type="button"
            onClick={handleSaveAmount}
            disabled={isLoadingAmount}
          >
            {isLoadingAmount && <Loader size={14} className="animate-spin" />}
            Save
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <label htmlFor="total" className="text-sm text-[#646464]">
              Total ({orderData.currencyCode === 'KRW' ? '₩' : '$'})
            </label>
            <input
              type="text"
              id="total"
              className="w-96 rounded-md border border-[#CCCCCC] px-2 py-1.5 text-right outline-none"
              value={totalAmount}
              onChange={handleTotalAmountChange}
              disabled={isLoadingAmount}
            />
          </div>

          <div className="flex items-center justify-between">
            <label htmlFor="discount" className="text-sm text-[#646464]">
              Discount Reason
            </label>
            <input
              type="text"
              id="discount"
              className="w-96 rounded-md border border-[#CCCCCC] px-2 py-1.5 text-right outline-none"
              placeholder="Enter discount reason"
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
              disabled={isLoadingAmount}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-[#F1F1F1] p-5">
        <h4 className="text-sm text-[#646464]">Quantity</h4>
        <div className="flex gap-1 text-sm text-[#1D1D1D]">
          <p>{totalQuantity}</p>
          <span>items</span>
        </div>
      </div>
    </div>
  );
};

export default OrderSummarySection;
